import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'
import { LocaleSwitcher } from './components/LocaleSwitcher'
import { NotificationsBell } from './components/NotificationsBell'
import { ThemeToggle } from './components/ThemeToggle'
import { authApi } from './lib/auth-api'
import { useAuthStore } from './stores/auth.store'
import type { Role } from '@cgp/shared'

const EMPTY_ROLES: readonly Role[] = []

const LoginPage = lazy(() => import('./routes/LoginPage'))
const ContractorHome = lazy(() => import('./routes/contractor/ContractorHome'))
const HseHome = lazy(() => import('./routes/hse/HseHome'))
const GateApp = lazy(() => import('./routes/gate/GateApp'))
const AdminHome = lazy(() => import('./routes/admin/AdminHome'))
const NotificationsInbox = lazy(() => import('./routes/notifications/NotificationsInbox'))

/**
 * Routing shell. Landing route is decided by role — a guard opens straight into
 * the scanner, because at the gate every extra tap costs queue time.
 */
function RoleLanding() {
  const roles = useAuthStore((state) => state.user?.roles ?? EMPTY_ROLES)
  if (roles.includes('SECURITY_GUARD')) return <Navigate to="/gate" replace />
  if (roles.includes('HSE_OFFICER')) return <Navigate to="/hse" replace />
  if (roles.includes('CONTRACTOR_ADMIN')) return <Navigate to="/contractor" replace />
  if (roles.includes('SYSTEM_ADMIN')) return <Navigate to="/admin" replace />
  return <Navigate to="/login" replace />
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken))
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function LoadingScreen() {
  const { t } = useTranslation('common')
  return (
    <div className="flex min-h-dvh items-center justify-center text-ink-muted dark:text-surface/70" role="status">
      {t('state.loading')}
    </div>
  )
}

/**
 * Logout control. Renders only when authenticated. The API call is best-effort
 * (see authApi.logout) so we always clear local state and land on /login even
 * if the request fails. Selector returns primitives only, never a new object,
 * to avoid the re-render churn flagged in bug #185.
 */
function LogoutButton() {
  const { t } = useTranslation('common')
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken))
  const clear = useAuthStore((state) => state.clear)
  const navigate = useNavigate()

  if (!isAuthenticated) return null

  const handleLogout = async () => {
    await authApi.logout()
    clear()
    navigate('/login')
  }

  return (
    <button
      type="button"
      onClick={handleLogout}
      aria-label={t('common:nav.logout')}
      title={t('common:nav.logout')}
      className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
    >
      <LogOut className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">{t('common:nav.logout')}</span>
    </button>
  )
}

export function App() {
  const isAuthenticated = useAuthStore((state) => Boolean(state.accessToken))

  return (
    <BrowserRouter>
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/80">
        <span className="flex items-center gap-1 font-display text-lg font-bold tracking-tight text-brand-600 dark:text-brand-400">
          CGP
          <span className="h-2 w-2 rounded-full bg-brand-500" aria-hidden="true" />
        </span>
        <div className="flex items-center gap-2">
          {isAuthenticated && <NotificationsBell />}
          <LogoutButton />
          <ThemeToggle />
          <LocaleSwitcher />
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl bg-surface px-4 py-6 dark:bg-surface-dark">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/contractor/*"
              element={
                <RequireAuth>
                  <ContractorHome />
                </RequireAuth>
              }
            />
            <Route
              path="/hse/*"
              element={
                <RequireAuth>
                  <HseHome />
                </RequireAuth>
              }
            />
            <Route
              path="/gate/*"
              element={
                <RequireAuth>
                  <GateApp />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/*"
              element={
                <RequireAuth>
                  <AdminHome />
                </RequireAuth>
              }
            />
            <Route
              path="/notifications"
              element={
                <RequireAuth>
                  <NotificationsInbox />
                </RequireAuth>
              }
            />
            <Route path="/" element={<RoleLanding />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </BrowserRouter>
  )
}
